<template>
  <el-col :sm="12" class="edit-user">
    <base-dialog :title="$t('user.editUser')" :openDialog="openEditDialog">
      <el-form
        :model="form"
        :rules="validator"
        ref="form"
        label-position="top"
        @submit.native.prevent
      >
        <el-row :gutter="50" class="row-input">
          <el-col :xs="24" :sm="10">
            <el-form-item :label="null">
              <span class="label"> {{ $t('user.username') }} </span>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="14">
            <el-form-item :label="null" prop="userName">
              <el-input
                class="input"
                v-model="form.userName"
                autocomplete="username"
                name="sks-user-name"
              >
                <i
                  slot="suffix"
                  class="el-input__icon el-icon-circle-close el-input__clear"
                  @click="form.userName = ''"
                  v-if="form.userName"
                ></i>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="50" class="row-input">
          <el-col :xs="24" :sm="10">
            <el-form-item :label="null">
              <span class="label"> {{ $t('user.name') }} </span>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="14">
            <el-form-item :label="null" prop="fullName">
              <el-input
                class="input"
                v-model="form.fullName"
                autocomplete="fullname"
                name="sks-user-fullname"
              >
                <i
                  slot="suffix"
                  class="el-input__icon el-icon-circle-close el-input__clear"
                  @click="form.fullName = ''"
                  v-if="form.fullName"
                ></i>
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="50" class="row-input">
          <el-col :xs="24">
            <span class="label">
              {{ $t('user.passwordSetting') }}
            </span>
          </el-col>
        </el-row>

        <el-row :gutter="50" class="row-input">
          <el-col :xs="24" :sm="10">
            <el-form-item :label="null">
              <span class="label">
                {{ $t('user.passwordNew') }}
              </span>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="14">
            <el-form-item :label="null" prop="password">
              <el-input
                :type="showPass.new ? 'text' : 'password'"
                v-model="form.password"
                autocomplete="new-password"
                class="input icon-password"
                name="sks-new-pass"
              >
                <img
                  :src="showPass.new ? showIcon : hiddenIcon"
                  slot="suffix"
                  alt=""
                  class="mtb-15 mlr-10 icon-pass"
                  @click="showPass.new = !showPass.new"
                />
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="50" class="row-input">
          <el-col :xs="24" :sm="10">
            <el-form-item :label="null" class="confirm-password">
              <span class="label">
                {{ $t('user.confirmNewPassword1') }}
              </span>

              <span class="label note-password">
                {{ $t('user.confirmNewPassword2') }}
              </span>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="14">
            <el-form-item :label="null" prop="confirmPassword">
              <el-input
                class="input icon-password"
                :type="showPass.check ? 'text' : 'password'"
                v-model="form.confirmPassword"
                autocomplete="confirm-password"
                name="sks-confirm-pass"
              >
                <img
                  :src="showPass.check ? showIcon : hiddenIcon"
                  slot="suffix"
                  alt=""
                  class="mtb-15 mlr-10 icon-pass"
                  @click="showPass.check = !showPass.check"
                />
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item>
          <span class="dialog-footer">
            <el-button
              class="btn btn--cancel"
              @click="handleCloseEditDialog('form')"
            >
              {{ $t('user.cancelBtn') }}
            </el-button>
            <el-button
              class="btn btn--submit"
              :disabled="editUserLoading"
              @click="onSubmit('form')"
            >
              {{ $t('user.editBtn') }}
            </el-button>
          </span>
        </el-form-item>
      </el-form>
    </base-dialog>
  </el-col>
</template>

<script>
import { mapActions } from 'vuex';

import { userService } from '@/services/user.service';
import { handleError, notifyMessage, redirectRouter } from '@/helper';
import { LIMIT, PAGE } from '@/constants/common';
import {
  checkFullWidth,
  maxName225,
  sizePassword,
  spacePassword,
  name,
} from '@/helper/validate';
import * as pageRouter from '@/constants/pageRouter';
import hiddenIcon from '@/assets/images/hide-pass.svg';
import showIcon from '@/assets/images/show-pass.svg';

const BaseDialog = () => import('@/components/Dialog');

export default {
  name: 'EditUserDialog',
  props: {
    openEditDialog: Boolean,
    user: Object,
  },
  components: { BaseDialog },
  data() {
    const validateNewPass = (rule, value, callback) => {
      if (!value) {
        return callback();
      }

      sizePassword(rule, value, callback);
      spacePassword(rule, value, callback);
      checkFullWidth(rule, value, callback);

      return callback();
    };

    const validateConfirmPass = (rule, value, callback) => {
      if (value !== this.form.password) {
        return callback(new Error(this.$t('validate.dontMatch')));
      }

      if (!value) {
        return callback();
      }

      sizePassword(rule, value, callback);
      spacePassword(rule, value, callback);
      checkFullWidth(rule, value, callback);

      return callback();
    };
    return {
      editUserLoading: false,
      showIcon,
      hiddenIcon,
      showPass: {
        new: false,
        check: false,
      },
      form: {
        userName: this.user.userName,
        fullName: this.user.fullName,
        password: '',
        confirmPassword: '',
      },
      validator: {
        userName: [{ validator: maxName225, trigger: 'blur' }],
        fullName: [{ validator: name, trigger: 'blur' }],
        password: [{ validator: validateNewPass, trigger: 'blur' }],
        confirmPassword: [
          {
            validator: validateConfirmPass,
            trigger: 'blur',
          },
        ],
      },
    };
  },
  watch: {
    user() {
      this.form.userName = this.user.userName;
      this.form.fullName = this.user.fullName;
    },
    'form.userName'() {
      if (this.$refs['form']) {
        this.$refs['form'].clearValidate('userName');
      }
    },
    'form.fullName'() {
      if (this.$refs['form']) {
        this.$refs['form'].clearValidate('fullName');
      }
    },
    'form.password'() {
      if (this.$refs['form']) {
        this.$refs['form'].clearValidate('password');
      }
    },
    'form.confirmPassword'() {
      if (this.$refs['form']) {
        this.$refs['form'].clearValidate('confirmPassword');
      }
    },
  },
  methods: {
    ...mapActions('user', ['getUserList']),
    handleCloseEditDialog(form) {
      this.$refs[form].resetFields();
      this.$emit('handleCloseEditDialog');
    },
    onSubmit(form) {
      this.$refs[form].validate(async (valid) => {
        if (valid) {
          this.editUserLoading = true;

          return await userService
            .edit(
              {
                ...this.form,
                confirmPassword: undefined,
                password: undefined,
                passwordNew: this.form.password ? this.form.password : null,
              },
              this.user.id
            )
            .then(() => {
              this.getUserList({
                page: PAGE,
                limit: LIMIT,
              });

              redirectRouter({
                path: pageRouter.USER,
                query: {
                  freeText: undefined,
                  page: undefined,
                },
              });
              this.$emit('handleCloseEditDialog');
              this.$refs[form].resetFields();
              notifyMessage(this.$t('common.editSuccess'), 'success');
            })
            .catch((error) => {
              handleError(error);
            })
            .finally(() => {
              this.editUserLoading = false;
            });
        }
      });
    },
  },
};
</script>

<style lang="scss" scope>
.edit-user {
  .el-input__suffix {
    img {
      width: 15px;
      height: 15px;
      cursor: pointer;
      margin-top: 12px;
    }
  }
  .label {
    font-size: 16px;
    font-weight: bold;
  }
  .row-input {
    margin-bottom: 20px;
  }

  .el-dialog__body {
    margin-top: 20px;
  }

  .el-dialog {
    width: 620px !important;
  }
  @media only screen and (max-width: 768px) {
    .dialog .el-dialog {
      width: calc(80% - 20px) !important;
    }
  }
  @media only screen and (max-width: 576px) {
    .dialog .el-dialog {
      width: calc(100% - 20px) !important;
    }
  }
  .confirm-password {
    margin-top: -5px;
    @media only screen and (min-width: 768px) {
      .note-password {
        display: block;
        margin-top: -20px;
        margin-left: 15px;
      }
    }
  }
}
</style>
